/**
 * Typisierte Modelle für Standort, Zeitreihe, Datenqualität,
 * Analysekonfiguration, Kennzahl und Herkunft.
 */

/** Version des gespeicherten Analysezustands (localStorage, JSON-Export). */
export const STATE_SCHEMA_VERSION = 1;
/** Version der Berechnungsdefinitionen (docs/methodik.md). */
export const CALC_VERSION = '1.0.0';

export type ModelId = 'era5_land' | 'era5';

export interface ModelInfo {
  id: ModelId;
  label: string;
  dataset: string;
  resolution: string;
  gridDeg: number;
  firstYear: number;
}

export const MODELS: Record<ModelId, ModelInfo> = {
  era5_land: {
    id: 'era5_land',
    label: 'ERA5-Land',
    dataset: 'ERA5-Land hourly (ECMWF/Copernicus C3S)',
    resolution: '0.1° (ca. 11 km)',
    gridDeg: 0.1,
    firstYear: 1950,
  },
  era5: {
    id: 'era5',
    label: 'ERA5',
    dataset: 'ERA5 hourly single levels (ECMWF/Copernicus C3S)',
    resolution: '0.25° (ca. 25 km)',
    gridDeg: 0.25,
    firstYear: 1940,
  },
};

/**
 * Herkunft einer Klimareihe. Für Ausbaustufe B sind Szenario, Modell und
 * Ensemblemitglied bereits getrennt vorgesehen, aber nicht implementiert.
 */
export type ClimateSource =
  | { kind: 'reanalysis'; model: ModelId }
  | { kind: 'projection'; scenario: string; gcm: string; member: string; downscaling: string };

export type GeocoderId = 'geo.admin.ch' | 'open-meteo-geocoding' | 'koordinaten' | 'csv-koordinaten';

export type ElevationSource = 'swissALTI3D' | 'open-meteo-dem90' | 'manuell';

/** Öffentliche Merkmale aus dem eidg. Gebäude- und Wohnungsregister (informativ). */
export interface GwrInfo {
  egid: string;
  gbauj: number | null;
  gbaup: number | null;
  gkat: number | null;
  gklas: number | null;
  gstat: number | null;
  gastw: number | null;
  gebf: number | null;
  garea: number | null;
  gwaerzh1: number | null;
  genh1: number | null;
  gwaerzw1: number | null;
  genw1: number | null;
  gexpdat: string | null;
  fetchedAt: string;
}

export interface Building {
  id: string;
  label: string;
  address?: string;
  egid?: string;
  country?: string;
  lat: number;
  lon: number;
  lv95?: { e: number; n: number };
  elevation?: { value: number; source: ElevationSource };
  timezone?: string;
  geocoder: GeocoderId;
  addedAt: string;
  gwr?: GwrInfo;
}

/** Zuordnung eines Gebäudes zu einer Rasterzelle (je Modell). */
export interface Placement {
  buildingId: string;
  model: ModelId;
  cellKey: string;
  cellLat: number;
  cellLon: number;
  timezone: string;
  /** Höhe, auf die korrigiert wird (Standorthöhe). */
  targetElevation: number;
  targetElevationSource: ElevationSource;
  discoveredAt: string;
}

export interface CellRef {
  key: string;
  model: ModelId;
  lat: number;
  lon: number;
}

export function cellKey(model: ModelId, lat: number, lon: number): string {
  return `${model}:${lat.toFixed(4)}:${lon.toFixed(4)}`;
}

export interface AnalysisConfig {
  model: ModelId;
  heightCorrection: boolean;
  hddBase: number;
  cddBase: number;
  hotDayThreshold: number;
  heatPeriodMinDays: number;
  nightStartHour: number;
  nightEndHour: number;
  warmNightThreshold: number;
  coolingRef: number;
  coolingMinDelta: number;
  coolingSeasonStartMonth: number;
  coolingSeasonEndMonth: number;
  referenceStart: number;
  referenceEnd: number;
  periodStart: number;
  periodEnd: number;
  hints: HintThresholds;
}

/** Schwellen der Hinweisregeln: konfigurierbare Projektannahmen, keine Normwerte. */
export interface HintThresholds {
  recentYears: number;
  cddRecentMin: number;
  hotDaysRecentMin: number;
  coolShareMin: number;
  warmNightsRecentMin: number;
  heatLongestMin: number;
  hddDropMin: number;
  cddRiseMin: number;
}

/** Status einer Stunde in einer Zellreihe. */
export const HourStatus = {
  NotLoaded: 0,
  Ok: 1,
  Missing: 2,
  Conflict: 3,
} as const;

export type FetchOrigin = 'netz' | 'browser-cache' | 'server-cache' | 'snapshot' | 'import';

export interface ChunkProvenance {
  provider: 'open-meteo';
  endpoint: string;
  model: ModelId;
  dataset: string;
  variable: 'temperature_2m';
  unit: '°C';
  timeMeaning: string;
  cellLat: number;
  cellLon: number;
  cellElevation: number | null;
  year: number;
  requestedStart: string;
  requestedEnd: string;
  deliveredStart: string | null;
  deliveredEnd: string | null;
  values: number;
  missing: number;
  duplicatesRemoved: number;
  conflicts: number;
  retrievedAt: string;
  origin: FetchOrigin;
  sha256: string;
  requestParams: Record<string, string>;
}

export type MetricKey =
  | 'hdd'
  | 'cdd'
  | 'warmNights'
  | 'coolHours'
  | 'coolKh'
  | 'hotDays'
  | 'heatEvents'
  | 'heatDays'
  | 'heatLongest'
  | 'tMean'
  | 'tropicalNights'
  | 'veryHotDays'
  | 'txx';

export interface YearMetrics {
  year: number;
  /** Alle benötigten Stunden des Jahres (inkl. Rand für Nächte) geladen. */
  loaded: boolean;
  /** Alle lokalen Tage und Nächte gültig. */
  complete: boolean;
  daysExpected: number;
  daysValid: number;
  nightsExpected: number;
  nightsValid: number;
  seasonNightsExpected: number;
  seasonNightsValid: number;
  /** Summe der Stunden gültiger Saisonnächte (Nenner der Kühlstunden). */
  seasonNightHours: number;
  hoursMissing: number;
  hoursConflict: number;
  hdd: number;
  cdd: number;
  hotDays: number;
  heatEvents: number;
  heatDays: number;
  heatLongest: number;
  warmNights: number;
  coolHours: number;
  coolKh: number;
  tMean: number | null;
  /** Tage mit Tagesminimum ≥ 20 °C (Standarddefinition Tropennacht). */
  tropicalNights: number;
  /** Tage mit Stundenmaximum ≥ 35 °C. */
  veryHotDays: number;
  /** Höchster Stundenwert des Jahres (gültige Tage). */
  txx: number | null;
}

export type ReferenceStatus = 'voll' | 'teil' | 'ungenuegend';

export interface ReferenceSummary {
  start: number;
  end: number;
  yearsTotal: number;
  yearsValid: number[];
  yearsExcluded: number[];
  status: ReferenceStatus;
  means: Partial<Record<MetricKey, number>>;
}

export interface BuildingResult {
  buildingId: string;
  cellKey: string;
  heightCorrectionK: number;
  years: YearMetrics[];
  reference: ReferenceSummary;
  lastCompleteYear: number | null;
}

export interface HeatEvent {
  startDate: string;
  endDate: string;
  days: number;
  maxT: number;
}

export interface DayRow {
  date: string;
  valid: boolean;
  loaded: boolean;
  hours: number;
  expected: number;
  mean: number | null;
  min: number | null;
  max: number | null;
}

export interface NightRow {
  date: string;
  valid: boolean;
  hours: number;
  expected: number;
  min: number | null;
  coolHours: number;
  coolKh: number;
  inSeason: boolean;
}

export interface YearDetail {
  buildingId: string;
  year: number;
  days: DayRow[];
  nights: NightRow[];
  events: HeatEvent[];
  metrics: YearMetrics;
}

export interface HourPoint {
  utc: string;
  local: string;
  t: number | null;
  status: 'ok' | 'fehlend' | 'widerspruch' | 'nicht geladen';
  inNight: boolean;
}
